import { useEffect, useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetSecondaryConnectionsQuery } from '@api/utrv';
import { loggerMessage } from '../../../logger';
import { CalculationIntegrationUtr } from '@g17eco/types/utrv-connections';
import { getUtrvConnections } from './utils';
import { IntegratedConnection } from './types';

export interface SmartMatchHint {
  providerName: string;
  metricName: string;
  value: number | string;
  unit: string;
  numberScale: string;
  connection: IntegratedConnection;
  valueListCode?: string;
}

const DEFAULT_PROVIDER_NAME = 'Emissions Tracker';

interface ProviderInfo {
  providerName: string;
  metricName: string;
  inlineSmartMatch: boolean;
}

const resolveProviderInfo = (
  connection: IntegratedConnection,
  integrationUtrs: CalculationIntegrationUtr[],
): ProviderInfo => {
  const integratedVariable = Object.values(connection.variables).find(
    (variable) => variable.integrationCode,
  );

  if (!integratedVariable) {
    return { providerName: DEFAULT_PROVIDER_NAME, metricName: '', inlineSmartMatch: false };
  }

  const integrationUtr = integrationUtrs.find((utr) => utr.code === integratedVariable.code);

  return {
    providerName: integrationUtr?.provider?.shortName ?? DEFAULT_PROVIDER_NAME,
    metricName: integrationUtr?.name ?? '',
    inlineSmartMatch: integrationUtr?.provider?.capabilities?.inlineSmartMatch === true,
  };
};

export const useSmartMatchHints = (utrvId: string | undefined): SmartMatchHint[] => {
  const { data, isError } = useGetSecondaryConnectionsQuery(utrvId ? { utrvId } : skipToken);

  useEffect(() => {
    if (isError) {
      loggerMessage('Failed to fetch secondary connections for smart match hints', { level: 'error' });
    }
  }, [isError]);

  return useMemo(() => {
    if (!data) {
      return [];
    }

    const { connections = [], integrationUtrs = [] } = data;
    const { integratedConnections } = getUtrvConnections(connections);

    return integratedConnections
      .map((connection) => {
        const { providerName, metricName, inlineSmartMatch } = resolveProviderInfo(connection, integrationUtrs);

        return {
          providerName,
          metricName,
          value: connection.value,
          unit: connection.unit ?? '',
          numberScale: connection.numberScale ?? '',
          connection,
          valueListCode: connection.valueListCode,
          inlineSmartMatch,
        };
      })
      .filter((hint) => hint.inlineSmartMatch);
  }, [data]);
};
